import React, { useState, useEffect, useCallback, useMemo } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import { colors } from "../../../assets/styles/theme";

import {
  CircularProgress,
} from "@mui/material";

import {
  Box,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormLabel,
  Divider,
  Button,
  Tabs,
  Tab,
  Tooltip,
  MenuItem,
  Switch,
  Alert,
  styled,
  DialogProps,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";

import DeleteIconSvg from "../../../assets/images/icon-delete.svg";
import AddIcon from "@mui/icons-material/Add";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CloseIcon from "@mui/icons-material/Close";

import FileItem from "../FileItem";

interface DownloadFileProps {
  open: any;
  handleCancel?: () => void;
  handleConfirm?: (selectedIds : any) => void;
  fileList: any;
}

const DownloadFile: React.FC<DownloadFileProps> = ({
  open,
  handleConfirm,
  handleCancel,
  fileList,
}) => {
  // modal popup
  const [fullWidth, setFullWidth] = React.useState(true);
  const [maxWidth, setMaxWidth] = React.useState<DialogProps["maxWidth"]>("lg");

  const [loader, setLoader] = React.useState(false);

  const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
      padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
      padding: theme.spacing(1),
    },
  }));

  // Use a ref to store selection state without triggering re-renders
  const selectedFilesRef = React.useRef<{ [key: string]: boolean }>({});
  const [, forceUpdate] = React.useState({});

  const [files, setFiles] = React.useState(fileList);


  React.useEffect(() => {
    setFiles(fileList)
  },[fileList])

  console.log("fileList", fileList);

  // Simple toggle function that updates the ref directly
  const handleToggleSelect = (id: string) => {
    selectedFilesRef.current[id] = !selectedFilesRef.current[id];
    forceUpdate({}); // Force a single render after changes
  };

  // Check if all selected without updating state
  const isAllSelected = () => {
    return (
      files.length > 0 &&
      files.every((file: any) => selectedFilesRef.current[file.id])
    );
  };

  // Toggle all selections with a single render
  const handleSelectAll = () => {
    if (isAllSelected()) {
      // Deselect all
      selectedFilesRef.current = {};
    } else {
      // Select all
      files.forEach((file: any) => {
        selectedFilesRef.current[file.id] = true;
      });
    }
    forceUpdate({}); // Force a single render after changes
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    console.log(`Delete file with id: ${id}`);

    // Remove the file from the local state
    setFiles((prevFiles : any) => prevFiles.filter((file : any) => file.id !== id));

    // Remove the file from the selectedFilesRef
    delete selectedFilesRef.current[id];

    // Force a re-render to update the UI
    forceUpdate({});
  };

  async function downloadFiles(filePaths: any, selectedIds :any) {
    // If only one file is provided, download it directly
    if (filePaths.length === 1) {
      const filePath = filePaths[0];
      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${filePath}`);
        }
        const blob = await response.blob();
        const filename = filePath.split("/").pop().split("?")[0];
        handleConfirm?.(selectedIds);
        setLoader(false);
        saveAs(blob, filename); // Download the single file
        console.log(`Downloaded file: ${filename}`);
      } catch (error) {
        setLoader(false);
        console.error("Error downloading file:", error);
      }
    }
    // If multiple files are provided, bundle them into a zip
    else if (filePaths.length > 1) {
      const zip = new JSZip();

      // Fetch each file and add it to the zip
      const fetchPromises = filePaths.map(async (filePath: any) => {
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${filePath}`);
        }
        const blob = await response.blob();
        const filename = filePath.split("/").pop().split("?")[0];
        zip.file(filename, blob);
      });

      try {
        // Wait for all files to be fetched and added to the zip
        await Promise.all(fetchPromises);

        // Generate the zip file
        const content = await zip.generateAsync({ type: "blob" });

        handleConfirm?.(selectedIds);
        setLoader(false);
        // Trigger the download
        saveAs(content, "download.zip");

        console.log("All files have been downloaded and zipped.");
      } catch (error) {
        setLoader(false);
        console.error("Error downloading files:", error);
      }
    } else {
      setLoader(false);
      console.error("No file paths provided.");
    }
  }

  const handleDownloadSelected = async () => {
    setLoader(true);
    const selectedIds = Object.keys(selectedFilesRef.current).filter(
      (id) => selectedFilesRef.current[id]
    );

    console.log("selectedIds1", selectedIds);

    if (selectedIds.length === 0) {
      alert("Please select at least one file to download");
      return;
    }

    // Convert IDs to numbers (since the `id` in the list is a number)
    const numericIds = selectedIds.map((id: any) => Number(id));

    // Extract filePaths for the given IDs
    const filePaths = files
      .filter((file: any) => numericIds.includes(file.id)) // Filter by IDs
      .map((file: any) => file.filePath); // Extract filePath

    console.log(filePaths);

    downloadFiles(filePaths, selectedIds);

    // Handle download logic here
  };

  // Check if any files are selected
  const isAnyFileSelected = Object.keys(selectedFilesRef.current).some(
    (id) => selectedFilesRef.current[id]
  );

  return (
    <>
      <Dialog
        onClose={(event, reason) => {
           if (reason === 'backdropClick') {
            return;
          }
        }}
        aria-labelledby="download-attachment-modal"
        open={open}
        slotProps={{
          backdrop: {
            onClick: (e) => e.stopPropagation()
          }
        }}
        fullWidth={true}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "440px",
          },
          "& .MuiDialogContent-root": {
            padding: 2,
          },
          "& .MuiDialogActions-root": {
            padding: 1,
          },
        }}
        className="digitalModal"
      >
        <DialogTitle
          sx={{ m: 0 }}
          id="download-attachment-modal"
          className="fs-16"
        >
          DOWNLOAD ATTACHMENTS
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 12,
            // color: theme.palette.grey[500],
            color: colors.lightBlack,
          })}
        >
          <CloseIcon sx={{ fontSize: "2rem" }} />
        </IconButton>

        <DialogContent dividers>
          <Box className="dowloadAttachWrapper">
            <FormGroup>
              {files.map((file: any) => (
                <Box key={file.id} className="d-flex justify-content-between align-items-center" sx={{mb: 1,}}>
                  <FormControlLabel
                    className="break-word"
                    control={
                      <Checkbox
                        size="small"
                        id={`file-${file.id}`}
                        checked={!!selectedFilesRef.current[file.id]}
                        onChange={() => handleToggleSelect(file.id)}
                      />
                    }
                    label={
                      file.name.length > 45 ? (
                        <Tooltip 
                          arrow 
                          title={file.name}
                        >
                          <span style={{ cursor: 'pointer', }}>
                            {file.name.substring(0, 45) + "..."}
                          </span>
                        </Tooltip>
                      ) : (
                        file.name
                      )                     
                    }
                  />
                  {false && <IconButton
                    aria-label="Delete"
                    size="medium"
                    className="iconBtn"
                    sx={{ ml: 1 }}
                    onClick={(e) => handleDelete(file.id, e)}
                  >
                    <img src={DeleteIconSvg} alt="Delete Icon" />
                  </IconButton>}
                </Box>
              ))}
            </FormGroup>
          </Box>
        </DialogContent>

        <DialogActions className="d-flex justify-content-between">
          <Button variant="outlined" size="small" onClick={handleSelectAll}>
            Select all
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleDownloadSelected}
            disabled={!isAnyFileSelected}
          >
            Download Selected {loader &&  <CircularProgress size="1rem" sx={{color: colors.primaryDark, ml: 1 }} />}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DownloadFile;
